import React from "react";
import styled from "@emotion/styled";
import { ProjectType } from "@prisma/client";
import { DialogModal } from "../DialogModal";
import { Typography } from "../Typography";
import * as Tabs from "../Tabs/Tabs";
import { ImpactType, PublicProjectLocation } from "@ecologi/shared";
import { ImpactCard } from "../ImpactCard";

export type DisplayProject = {
  type: ProjectType;
  slug: string;
  title: string;
  quantity: string;
  location?: PublicProjectLocation;
};

const StyledModal = styled(DialogModal)`
  height: 796px;
`;

const TitleText = styled(Typography)`
  font-size: 24px !important;
  @media ${(props) => props.theme.bp.tabletDown} {
    font-size: 18px !important;
  }
`;

const TabContent = styled(Tabs.Content)`
  &[data-state="active"] {
    margin-top: 32px;
    display: block;
  }
`;

const TitleContainer = styled.div`
  margin-top: 8px;
  max-width: 556px;
  margin-bottom: 46px;
  @media ${(props) => props.theme.bp.tabletDown} {
    margin-bottom: 24px;
  }
`;

const PendingTitleContainer = styled.div`
  margin-top: 16px;
`;

const ScrollTabContent = styled(TabContent)`
  &[data-state="active"] {
    display: flex;
    margin-top: 24px;
    flex-direction: column;
    gap: 16px;
    &:last-child {
      padding-bottom: 56px;
    }
  }
`;

export const DarkLinkText = styled(Typography)`
  color: ${({ theme }) => theme.colors.charcoal};
`;

const StyledTabs = styled(Tabs.Root)`
  display: flex;
  flex-direction: column;
`;

const StyledTabList = styled(Tabs.List)`
  position: sticky;
  z-index: 1;
  top: -4px;
  background: ${({ theme }) => theme.colors.snowdrop};
`;

const TITLE_ID = "how-calculated-title";

const impactTypeLabels: Record<ImpactType, string> = {
  [ImpactType.TREES]: "Why trees?",
  [ImpactType.CARBON_OFFSETS]: "Why avoidance?",
  [ImpactType.CARBON_REMOVAL]: "Why removals?",
  [ImpactType.HABITAT_RESTORATION]: "Why habitat restoration?",
};

export const HowCalculatedModal = ({
  isOpen,
  onClose,
  projects,
  title,
  pendingTitle,
  noImpactTitle,
  noImpactSlot,
  type,
  projectsIntro,
  explanation,
  pendingTotal,
}: {
  isOpen: boolean;
  onClose: () => void;
  projects: DisplayProject[];
  title: React.ReactNode;
  pendingTitle: React.ReactNode;
  pendingTotal: number;
  type: ImpactType;
  explanation: React.ReactNode;
  projectsIntro?: React.ReactNode;
  noImpactTitle: string;
  noImpactSlot?: React.ReactNode;
}) => {
  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      scrollable
      dataTestId="how-calculated-modal"
    >
      <TitleContainer id={TITLE_ID}>
        <TitleText use="body1" as={"h2"}>
          {projects.length > 0 ? title : noImpactTitle}
        </TitleText>
        {pendingTotal > 0 && (
          <PendingTitleContainer>{pendingTitle}</PendingTitleContainer>
        )}
      </TitleContainer>
      <StyledTabs
        defaultValue="projects"
        onValueChange={() => {
          const section = document.querySelector(`#${TITLE_ID}`);
          section?.scrollIntoView();
        }}
      >
        <StyledTabList aria-label="Browse your impact">
          <Tabs.Trigger value="projects">Projects supported</Tabs.Trigger>
          <Tabs.Trigger value="explanation">
            {impactTypeLabels[type]}
          </Tabs.Trigger>
        </StyledTabList>
        <ScrollTabContent value="projects">
          {projects.length > 0 ? (
            <>
              {projectsIntro}
              {projects.map((project, i) => (
                <ImpactCard
                  location={project.location}
                  projectType={project.type}
                  key={i}
                  quantity={project.quantity}
                  slug={project.slug}
                  title={project.title}
                  impactType={type}
                />
              ))}
            </>
          ) : (
            noImpactSlot && noImpactSlot
          )}
        </ScrollTabContent>
        <ScrollTabContent value="explanation">{explanation}</ScrollTabContent>
      </StyledTabs>
    </StyledModal>
  );
};
